// components/FavoriteFighterPicker.tsx
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Fight } from '../../../../../../corelogic/domain/models/fight.model';
import { Fighter } from '../../../../../../corelogic/domain/models/fighter.model';
import { Gender } from '../../../../../../corelogic/domain/models/gender.enum';
import {
  extractWeightValue,
  getDivisionsForDropdown,
  weightDivisions,
} from '../../../../../../corelogic/domain/utils/weightUtils';
import FighterDetailsModal from '../../Shared/FighterDetailsModal/FighterDetailsModal';
import { FighterCard } from './FighterCard';

// Style objects
const styles = {
  container: {
    height: 'auto',
    minHeight: {
      xs: '200px',
      sm: '250px',
      md: '300px',
      lg: '600px',
      xl: '700px',
    },
    maxHeight: {
      xs: '85vh',
      sm: '85vh',
      md: '70vh',
      lg: '80vh',
      xl: '90vh',
    },
    p: 3,
    width: '100%',
    maxWidth: '100%',
    borderRadius: 2,
    bgcolor: 'rgba(20, 20, 20, 0.9)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
    boxSizing: 'border-box',
    position: 'relative',
    paddingBottom: '36px',
  },
  sectionTitle: {
    fontFamily: 'Rubik Mono One',
    color: '#ffffff',
    fontSize: 'clamp(1rem, 1.5vw, 1.3rem)',
    wordBreak: 'break-word',
    width: 'calc(100% - 100px)',
    marginRight: '8px',
    flex: '1',
    textOverflow: 'ellipsis',
  },
  fighterList: {
    flex: 1,
    overflowY: 'auto',
    flexGrow: 1,
    minHeight: '300px',
    bgcolor: 'rgba(10, 10, 10, 0.5)',
    borderRadius: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    mb: 3,
    alignItems: 'stretch',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.4)',
      },
    },
  },
  filterFormControl: {
    minWidth: '100%',
    '& .MuiOutlinedInput-root': {
      color: 'white',
      height: '36px',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: '#cc0000',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#cc0000',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      transform: 'translate(14px, 9px) scale(1)',
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'left',
      '& legend': {
        width: 'auto !important',
        padding: '0 8px',
      },
    },
    '& .MuiSelect-select': {
      paddingTop: '8px',
      paddingBottom: '8px',
    },
  },
  searchInput: {
    '& .MuiOutlinedInput-root': {
      color: 'white',
      height: '36px',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: '#cc0000',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#cc0000',
      },
    },
    '& .MuiInputAdornment-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  filtersContainer: {
    mb: 2,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'rgba(20, 20, 20, 0.95)',
    pt: 1,
    pb: 1,
  },
  noFightersText: {
    color: '#888888',
    textAlign: 'center',
    p: 3,
    mb: 2,
    mt: 2,
  },
  letterHeader: {
    p: 1,
    bgcolor: 'transparent',
  },
  letterNavigation: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'flex-start',
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: '4px 0',
    height: '40px',
    bgcolor: 'rgba(20, 20, 20, 0.7)',
    width: '100%',
    scrollBehavior: 'smooth',
    scrollPaddingLeft: '0',
    '&::-webkit-scrollbar': {
      height: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.3)',
      borderRadius: 4,
    },
  },
  letterButton: (isActive: boolean) => ({
    minWidth: '30px',
    fontSize: '0.6rem',
    height: '100%',
    padding: '0 6px',
    margin: '0',
    width: '100%',
    fontWeight: isActive ? 'bold' : 'normal',
    borderRadius: 0,
    backgroundColor: isActive ? 'rgba(204, 0, 0, 0.2)' : 'transparent',
    color: isActive ? 'white' : 'grey.400',
    borderRight: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(204, 0, 0, 0.2)',
    },
  }),
  letterButtonGroup: {
    display: 'flex',
    flexWrap: 'nowrap',
    height: '100%',
    minWidth: 'min-content',
    justifyContent: 'flex-start',
    '& .MuiButtonGroup-grouped:not(:last-of-type)': {
      borderRight: '1px solid rgba(60, 60, 60, 0.8)',
    },
  },
  cardRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  favoriteFilterButton: (isActive: boolean) => ({
    color: isActive ? 'white' : 'rgba(255, 255, 255, 0.7)',
    borderColor: isActive ? '#cc0000' : 'rgba(255, 255, 255, 0.3)',
    bgcolor: isActive ? '#cc0000' : 'rgba(204, 0, 0, 0.1)',
    height: '36px',
    position: 'relative',
    paddingLeft: '36px',
    width: '100%',
    textTransform: 'none',
    justifyContent: 'flex-start',
    fontWeight: isActive ? 'bold' : 'normal',
    '& .MuiButton-startIcon': {
      position: 'absolute',
      left: '12px',
      color: isActive ? 'white' : '#cc0000',
    },
    '&:hover': {
      bgcolor: isActive ? '#b30000' : 'rgba(204, 0, 0, 0.1)',
      borderColor: '#cc0000',
    },
    '&:hover .MuiButton-startIcon': {
      color: isActive ? 'white' : '#cc0000',
    },
  }),
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 3,
  },
  saveButtonContainer: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    zIndex: 20,
    minWidth: '60px',
    height: '32px',
    px: 1.5,
    py: 0.5,
    fontSize: 'clamp(0.8rem, 1.5vw, 1rem)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
  },
  comingSoonBadge: {
    fontSize: '0.7rem',
    color: '#ffd700',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: '2px 6px',
    borderRadius: '4px',
    border: '1px solid #ffd700',
  },
  saveButtonNormal: {
    bgcolor: '#8B0000',
    color: 'white',
    '&:hover': {
      bgcolor: '#aa0000',
    },
    '&.Mui-disabled': {
      bgcolor: 'rgba(204, 0, 0, 0.3)',
      color: 'rgba(255, 255, 255, 0.5)',
    },
    ml: 'auto',
    minWidth: '80px',
    transition: 'all 0.3s ease',
  },
  saveButtonSuccess: {
    bgcolor: '#009900',
    color: 'white',
    '&:hover': {
      bgcolor: '#007700',
    },
    ml: 'auto',
    minWidth: '80px',
    transition: 'all 0.3s ease',
  },
};

interface FavoriteFighterPickerProps {
  fighters: Fighter[];
  fights: Fight[];
}

// TODO: Add Favorite FIghter State API call, add save to leave network component, add isupdated variable

const FavoriteFighterPicker = ({
  fighters: allFighters,
  fights: allFights,
}: FavoriteFighterPickerProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [weightClassFilter, setWeightClassFilter] = useState('all');
  const [favoriteFighters, setFavoriteFighters] = useState<Fighter[]>([]);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [selectedFighter, setSelectedFighter] = useState<Fighter | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<string>('A');
  const [visibleCount, setVisibleCount] = useState(20);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const letterRefs = useRef<Record<string, HTMLElement | null>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Get unique weight classes for filter dropdown
  const weightClasses = getDivisionsForDropdown();

  // Sort fighters alphabetically and by favorites
  const alphabetizedFilteredFighters = useMemo(() => {
    let filtered = allFighters;

    // Apply favorites filter
    if (showOnlyFavorites) {
      filtered = filtered.filter((f) =>
        favoriteFighters.some((fav) => fav.id === f.id),
      );
    }

    // Apply search filter
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((f) =>
        `${f.firstname} ${f.lastname}`.toLowerCase().includes(lowerSearchTerm),
      );
    }

    // Apply gender filter
    if (genderFilter && genderFilter !== 'all') {
      filtered = filtered.filter(
        (fighter) =>
          (genderFilter === 'M' && fighter.gender === Gender.M) ||
          (genderFilter === 'F' && fighter.gender === Gender.F),
      );
    }

    // Apply weight class filter
    if (weightClassFilter && weightClassFilter !== 'all') {
      const filterWeightValue = parseFloat(weightClassFilter);

      filtered = filtered.filter((fighter) => {
        // Extract numeric value from fighter weight
        const fighterWeightValue = extractWeightValue(fighter.weight);

        // If we can't determine fighter weight, don't include in filtered results
        if (fighterWeightValue === null) return false;

        // For exact division match, the fighter weight must be:
        // 1. Greater than the previous division's max weight (if any)
        // 2. Less than or equal to the selected division's max weight
        const previousDivisionIndex =
          weightDivisions.findIndex(
            (div) => div.maxWeight.toString() === weightClassFilter,
          ) - 1;

        const previousMaxWeight =
          previousDivisionIndex >= 0
            ? weightDivisions[previousDivisionIndex].maxWeight
            : 0;

        return (
          fighterWeightValue > previousMaxWeight &&
          fighterWeightValue <= filterWeightValue
        );
      });
    }

    // Special handling for search results
    const isSearching = !!searchTerm && searchTerm.length > 0;

    // Group fighters by first letter of last name
    const groups: Record<string, Fighter[]> = {};

    // Only include favorites group when not filtering to show only favorites
    if (!showOnlyFavorites) {
      groups['fav'] = [];
    }

    // Initialize A-Z groups
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('').forEach((letter) => {
      groups[letter] = [];
    });

    // If searching, show results at the top without letter grouping
    if (isSearching) {
      // Create result structure with just search results
      const result: {
        letter: string;
        fighters: Fighter[];
        startIndex: number;
      }[] = [];

      // Add a special "Search Results" section
      if (filtered.length > 0) {
        result.push({
          letter: 'Search Results',
          fighters: filtered,
          startIndex: 0,
        });
      }

      // Return early with just search results
      return result;
    }

    // Fill groups with fighters
    filtered.forEach((fighter) => {
      // First add to favorites if applicable
      if (
        !showOnlyFavorites &&
        favoriteFighters.some((f) => f.id === fighter.id)
      ) {
        if (groups['fav']) {
          groups.fav.push(fighter);
        }
      }

      // Then add to letter group
      const lastName =
        fighter.lastname && typeof fighter.lastname === 'string'
          ? fighter.lastname
          : '';
      const firstLetter = lastName ? lastName.charAt(0).toUpperCase() : '#';
      if (groups[firstLetter]) {
        groups[firstLetter].push(fighter);
      } else {
        groups['#'].push(fighter); // Non-alphabetic or missing names
      }
    });

    // Sort fighters within each group
    Object.values(groups).forEach((fighters) => {
      fighters.sort((a, b) =>
        `${a.firstname} ${a.lastname}`.localeCompare(
          `${b.firstname} ${b.lastname}`,
        ),
      );
    });

    // Convert to array with indices for virtualization
    const result: {
      letter: string;
      fighters: Fighter[];
      startIndex: number;
    }[] = [];
    let currentIndex = 0;

    // Only include the 'fav' group if it exists and has fighters
    if (!showOnlyFavorites && groups['fav'] && groups['fav'].length > 0) {
      const existingFavGroup = result.find((g) => g.letter === 'fav');
      if (!existingFavGroup) {
        result.push({
          letter: 'fav',
          fighters: groups['fav'],
          startIndex: currentIndex,
        });
        currentIndex += groups['fav'].length;
      }
    }

    // Add all other groups
    Object.entries(groups).forEach(([letter, fighters]) => {
      if (letter !== 'fav' && fighters.length > 0) {
        result.push({
          letter,
          fighters,
          startIndex: currentIndex,
        });
        currentIndex += fighters.length;
      }
    });

    // Check if result is empty - if so, add a message
    if (result.length === 0 && !isSearching) {
      console.warn('No fighters found in any group - possible data issue');
    }

    return result;
  }, [
    favoriteFighters,
    allFighters,
    searchTerm,
    showOnlyFavorites,
    genderFilter,
    weightClassFilter,
  ]);

  const addToFavorites = (fighter: Fighter) => {
    // Check if fighter is already in favorites
    if (!favoriteFighters.some((f) => f.id === fighter.id)) {
      setFavoriteFighters([...favoriteFighters, fighter]);
    }
  };

  const removeFromFavorites = (fighterId: string) => {
    setFavoriteFighters(favoriteFighters.filter((f) => f.id !== fighterId));
  };

  const handleFighterClick = (fighter: Fighter) => {
    setSelectedFighter(fighter);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSaveFavorites = async () => {
    try {
      setIsSaving(true);
      setSaveSuccess(false);

      // Simulate API call (replace with actual API when ready)
      await new Promise((resolve) => setTimeout(resolve, 1000));
      console.log('Favorites saved:', favoriteFighters);

      // Show success state
      setSaveSuccess(true);

      // Reset success state after 2 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error saving favorites:', error);
      // Handle error state if needed
    } finally {
      setIsSaving(false);
    }
  };

  // UseEffect to force initial scroll position to the start
  useEffect(() => {
    // Reset the scroll position of the alphabet container
    const alphabetContainer = document.querySelector(
      '[data-alphabet-navigation]',
    );
    if (alphabetContainer) {
      alphabetContainer.scrollLeft = 0;
    }
  }, []);

  // Handle letter click
  const scrollToLetter = (letter: string) => {
    setActiveIndex(letter);

    const element = document.getElementById(`section-${letter}`);
    if (element) {
      // Add a small offset to ensure the letter header is fully visible
      const headerHeight = 50;
      const filtersHeight = 180;
      const totalOffset = headerHeight + filtersHeight;
      const container = scrollContainerRef.current;

      if (container) {
        // Calculate the scroll position with offset
        const elementPosition = element.offsetTop - totalOffset;

        // Scroll to the element with the offset
        container.scrollTo({
          top: elementPosition,
          behavior: 'smooth',
        });
      }
    }
  };
  // Function to handle scroll events
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Check if scrolled near bottom (within 200px)
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      200;

    // Load more if near bottom and more fighters to show
    if (isNearBottom) {
      const totalItems = alphabetizedFilteredFighters.reduce(
        (sum, group) => sum + group.fighters.length + 1,
        0,
      );

      if (visibleCount < totalItems) {
        setVisibleCount((prev) => Math.min(prev + 20, totalItems));
      }
    }
  }, [alphabetizedFilteredFighters, visibleCount]);

  // Set up scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const handleToggleFavorite = (fighter: Fighter) => {
    const isInFavorites = favoriteFighters.some((f) => f.id === fighter.id);
    if (isInFavorites) {
      removeFromFavorites(fighter.id);
    } else {
      addToFavorites(fighter);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Box sx={styles.saveButtonContainer}>
          <Typography sx={styles.comingSoonBadge}>Coming Soon</Typography>
          <Button
            variant="contained"
            startIcon={saveSuccess ? <CheckCircleIcon /> : <SaveIcon />}
            onClick={handleSaveFavorites}
            disabled={true}
            // old: disabled={isSaving || favoriteFighters.length === 0}
            size="small"
            sx={
              saveSuccess ? styles.saveButtonSuccess : styles.saveButtonNormal
            }
          >
            {saveSuccess ? 'Saved' : 'Save'}
          </Button>
        </Box>

        <Typography sx={styles.sectionTitle}>Favorite Fighters</Typography>
      </Box>
      {/* Search and Filters Section */}
      <Box sx={styles.filtersContainer}>
        {/* Search Bar - Full Width on its own line */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search fighters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={styles.searchInput}
            size="small"
          />
        </Box>

        {/* All Filters on one line */}
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={4}>
            <FormControl fullWidth sx={styles.filterFormControl} size="small">
              <InputLabel id="gender-select-label" shrink>
                Gender
              </InputLabel>
              <Select
                value={genderFilter}
                labelId="gender-select-label"
                label="Gender"
                notched
                onChange={(e) => setGenderFilter(e.target.value)}
                displayEmpty
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="M">Male</MenuItem>
                <MenuItem value="F">Female</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth sx={styles.filterFormControl} size="small">
              <InputLabel id="division-select-label" shrink>
                Division
              </InputLabel>
              <Select
                value={weightClassFilter}
                labelId="division-select-label"
                label="Division"
                notched
                onChange={(e) => setWeightClassFilter(e.target.value)}
                displayEmpty
              >
                <MenuItem value="all">All</MenuItem>
                {weightClasses.slice(1).map((division) => (
                  <MenuItem key={division.value} value={division.value}>
                    {division.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <Button
              fullWidth
              startIcon={
                showOnlyFavorites ? <FavoriteIcon /> : <FavoriteBorderIcon />
              }
              onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
              size="small"
              sx={styles.favoriteFilterButton(showOnlyFavorites)}
            >
              FAVORITES
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Virtualized fighter list */}
      <Box
        ref={scrollContainerRef}
        id="fighter-list-container"
        sx={styles.fighterList}
      >
        {alphabetizedFilteredFighters.map((group) => (
          <React.Fragment key={group.letter}>
            {/* Letter header */}
            <Box
              ref={(el) =>
                (letterRefs.current[group.letter] = el as HTMLElement)
              }
              sx={styles.letterHeader}
              id={`section-${group.letter}`}
            >
              <Typography fontWeight="bold">
                {group.letter === 'fav' ? '❤️ Favorites' : group.letter}
              </Typography>
            </Box>

            {/* Only render visible fighters */}
            <Box sx={styles.cardRow}>
              {group.fighters.slice(0, visibleCount).map((fighter) => (
                <FighterCard
                  key={fighter.id}
                  fighter={fighter}
                  isFavorite={favoriteFighters.some((f) => f.id === fighter.id)}
                  onToggleFavorite={() => handleToggleFavorite(fighter)}
                  onClick={() => handleFighterClick(fighter)}
                />
              ))}
            </Box>
          </React.Fragment>
        ))}
      </Box>
      {alphabetizedFilteredFighters.length === 0 && (
        <Typography sx={styles.noFightersText}>
          {showOnlyFavorites
            ? "You haven't selected any favorite fighters yet"
            : 'No fighters found matching your criteria'}
        </Typography>
      )}
      {/* Alphabetical index */}
      <Box sx={styles.letterNavigation} data-alphabet-navigation>
        <ButtonGroup variant="text" size="small" sx={styles.letterButtonGroup}>
          <Button
            onClick={() => scrollToLetter('fav')}
            sx={styles.letterButton(activeIndex === 'fav')}
          >
            ❤️
          </Button>
          {'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('').map((letter) => (
            <Button
              key={letter}
              onClick={() => scrollToLetter(letter)}
              disabled={
                !alphabetizedFilteredFighters.some((g) => g.letter === letter)
              }
              sx={styles.letterButton(activeIndex === letter)}
            >
              {letter}
            </Button>
          ))}
        </ButtonGroup>
      </Box>
      {/* Fighter Details Modal */}
      <FighterDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        fighter={selectedFighter}
        fights={allFights}
      />
    </Box>
  );
};

export default FavoriteFighterPicker;
